'use client';

import React, { useMemo } from 'react';
import dynamic from 'next/dynamic';
import type { PortableTextBlock } from 'sanity';

import NavBarV2 from '@/components/global/partials/HeaderV2';
import type { PageFabioAffiliateSchema } from '@/sanity/pages/PageFabioAffiliateTemplate';
import SummerSaleTopBanner from '@/components/global/SummerSaleTopBanner';
import SummerSalePopup from '@/components/global/SummerSalePopup';
import AffiliateBanners from '@/components/global/AffiliateBanners';
import LaunchEventWidget from '@/components/global/LaunchEventWidget';
import TradingCompetitionPopup from '@/components/global/TradingCompetitionPopup';
import { useLaunchEventLive } from '@/hooks/useLaunchEventLive';
import {
  applyAffiliateDiscountToLabel,
  discountedPriceFrom,
  withAffiliateOfferDiscount,
} from '@/lib/affiliateDiscount';
import {
  getNewOfferPercent,
  resolveOfferLocale,
  rewriteCouponText,
  rewriteOfferInlinePairRichtext,
  rewriteOfferRichtextAcrossSpans,
  type OfferLocale,
} from '@/lib/affiliateNewOffer';

import HeroSection from '../pageComponents/fabioAffiliate/HeroSection';
import HeroSectionV2 from '../pageComponents/fabioAffiliate/HeroSectionV2';
import VipDealSection from '../pageComponents/fabioAffiliate/VipDealSection';
import IntroSection from '../pageComponents/fabioAffiliate/IntroSection';
import DealsSection from '../pageComponents/fabioAffiliate/DealsSection';
import StepsSection from '../pageComponents/fabioAffiliate/StepsSection';
import TradersSaySection from '../pageComponents/fabioAffiliate/TradersSaySection';
import ComparisonSection from '../pageComponents/fabioAffiliate/ComparisonSection';
import TestimonialsSection from '../pageComponents/fabioAffiliate/TestimonialsSection';
import FAQSection from '../pageComponents/fabioAffiliate/FAQSection';
import FinalCTASection from '../pageComponents/fabioAffiliate/FinalCta';
import FooterFabio from '../global/partials/FooterFabioPage';
import ScrollingBanner from '../pageComponents/global/ScrollingBanner';
import {
  PricingHeroBlock,
  PricingTiersBlock,
  PricingComparisonBlock,
  PricingPlatformsBlock,
} from '../pageComponents/pricingSectionV2';
import NewPricingSection from '../pageComponents/newPricing/NewPricingSection';

/**
 * On affiliate pages every purchase must go through the partner's own hero CTA
 * link — it carries the `?aff=` / coupon params that attribute the sale. So we
 * rewrite the href of every tier CTA (challenge, specs-toggle secondary and
 * funded) to that URL, keeping titles, variants and everything else from the
 * shared Pricing Section V2 document.
 *
 * This is a page-level override on a copy of the data: the Sanity document is
 * untouched and keeps its per-product checkout links for every other page.
 */
function withAffiliatePricingCta(
  section: PageFabioAffiliateSchema['pricingSectionV2'],
  url?: string,
): PageFabioAffiliateSchema['pricingSectionV2'] {
  if (!section || !url) return section;

  const withHref = <T extends { href?: string } | undefined>(cta: T): T =>
    cta ? ({ ...cta, href: url } as T) : cta;

  return {
    ...section,
    assets: section.assets?.map((asset) => ({
      ...asset,
      categories: asset.categories?.map((category) => ({
        ...category,
        tiers: category.tiers?.map((tier) => ({
          ...tier,
          ctaPrimary: withHref(tier.ctaPrimary),
          secondaryCtaPrimary: withHref(tier.secondaryCtaPrimary),
          fundedCtaPrimary: withHref(tier.fundedCtaPrimary),
        })),
      })),
    })),
  };
}

function richtextToText(
  blocks: PortableTextBlock[] | undefined | null,
): string {
  if (!Array.isArray(blocks)) return '';
  return blocks
    .map((block: any) =>
      Array.isArray(block?.children)
        ? block.children.map((child: any) => child?.text ?? '').join('')
        : '',
    )
    .join(' ');
}

/**
 * The percentage and language driving the unified offer copy, or undefined when
 * the page should keep its stored wording — either because no discount is set
 * (see withAffiliateOfferDiscount), or because none of the old split-discount
 * wording ("70% off Challenges. 40% off Instant Funding.") is present.
 *
 * The page advertises one number across both products while its deal cards are
 * CFD/crypto priced and its pricing section will be futures, so the lower of the
 * two funding types wins — it can never promise more than checkout grants.
 */
function resolveNewOffer(
  data: PageFabioAffiliateSchema,
): { percent: number; locale: OfferLocale } | undefined {
  const percents = (['futures', 'crypto'] as const)
    .map((fundingType) =>
      getNewOfferPercent(data?.pricingV2Discount, fundingType),
    )
    .filter((value): value is number => value != null);
  if (!percents.length) return undefined;

  const locale = resolveOfferLocale([
    richtextToText(data?.heroV2ChipText),
    richtextToText(data?.heroChipText),
    richtextToText(data?.finalCtaHeading),
    ...(data?.stepsItems ?? []).map((item) => item.text),
  ]);
  return locale ? { percent: Math.min(...percents), locale } : undefined;
}

/**
 * Rewrites the old split-discount copy ("70% off Challenges. 40% off Instant
 * Funding.") into the unified offer, so every percentage on the page follows
 * the discount from withAffiliateOfferDiscount instead of the stored text.
 * The Sanity document is untouched; this is a page-level override on a copy.
 *
 * Deal-card prices are recomputed from their own regular price for the same
 * reason: a card advertising 80% must not keep a price struck at 70%.
 */
function withNewOfferCopy(
  data: PageFabioAffiliateSchema,
  offer: { percent: number; locale: OfferLocale } | undefined,
): PageFabioAffiliateSchema {
  if (!offer) return data;
  const { percent, locale } = offer;

  const pair = <T extends PortableTextBlock[] | undefined | null>(
    blocks: T,
  ): T => rewriteOfferRichtextAcrossSpans(blocks, percent, locale);

  return {
    ...data,
    heroChipText: pair(data.heroChipText),
    heroV2ChipText: pair(data.heroV2ChipText),
    finalCtaHeading: pair(data.finalCtaHeading),

    // Both numbers on the VIP banner ("70 % OFF all Challenges" / "40 % OFF all
    // Funding") become the single percentage.
    vipDealItems: data.vipDealItems
      ? data.vipDealItems.map((item) =>
          item.unit?.includes('%') ? { ...item, value: String(percent) } : item,
        )
      : data.vipDealItems,

    dealsItems: data.dealsItems
      ? data.dealsItems.map((deal) => ({
          ...deal,
          tag: applyAffiliateDiscountToLabel(deal.tag, percent),
          discountText: applyAffiliateDiscountToLabel(
            deal.discountText,
            percent,
          ),
          priceCurrent:
            discountedPriceFrom(deal.priceOriginal, percent) ??
            deal.priceCurrent,
        }))
      : data.dealsItems,

    stepsItems: data.stepsItems
      ? data.stepsItems.map((item) => ({
          ...item,
          text: rewriteCouponText(item.text, percent, locale),
        }))
      : data.stepsItems,

    faqItems: data.faqItems
      ? data.faqItems.map((item) => ({
          ...item,
          answer: rewriteOfferInlinePairRichtext(item.answer, percent, locale),
        }))
      : data.faqItems,
  };
}

export default function PageFabioAffiliateJSX({
  data: rawData,
}: {
  data: PageFabioAffiliateSchema;
}) {
  const discountedData = withAffiliateOfferDiscount(rawData);
  const data = withNewOfferCopy(
    discountedData,
    resolveNewOffer(discountedData),
  );

  const summerSaleCtaUrl =
    data?.heroCTA?.cta?.link?.url ?? data?.summerSale?.ctaUrl ?? '#';

  // Hero V2 carries its own CTA button; both currently point at the same
  // partner link, but the banners must follow whichever hero is rendered.
  const affiliateCtaUrl =
    (data?.heroVersion === 'v2'
      ? data?.heroV2CTA?.cta?.link?.url
      : undefined) ?? data?.heroCTA?.cta?.link?.url;

  // The launch widget and the competition pop-up share the bottom-left
  // corner; the launch campaign wins while it runs, as on the homepage.
  const { live: launchEventLive } = useLaunchEventLive(
    data?.launchEvent,
    data?.settings?.language,
  );

  // Deliberately `affiliateCtaUrl` and not `summerSaleCtaUrl`: the price cards
  // must follow the same hero that is rendered, and if a page has no hero CTA
  // at all we keep each tier's own product link rather than pointing every card
  // at an unrelated fallback.
  const pricingSection = withAffiliatePricingCta(
    data?.pricingSectionV2,
    affiliateCtaUrl,
  );

  // No deal-tables discriminator on this template, so "Both" — and an empty
  // asset key — means "show every asset with the tabs" rather than guessing one.
  const pricingAssetKey = data?.pricingSectionV2AssetKey;
  const pricingFilter =
    pricingAssetKey && pricingAssetKey !== 'both'
      ? { assetKey: pricingAssetKey }
      : undefined;
  const marqueeItems = useMemo(() => {
    if (!data?.heroPills?.length) return null;
    return [0, 1].flatMap((copyIndex) =>
      data.heroPills!.map((item, index) => (
        <div
          key={`${copyIndex}-${index}`}
          className="flex flex-shrink-0 items-center"
          aria-hidden={copyIndex === 1 ? true : undefined}
        >
          <p className="m-0 font-mono leading-none uppercase whitespace-nowrap text-black/60 font-[500] tracking-[0.04rem]">
            {item.text}
          </p>
          <div className="flex-shrink-0 w-[.5rem] h-[.5rem] bg-[#228E71] mx-[5rem]" />
        </div>
      )),
    );
  }, [data?.heroPills]);

  return (
    <main className="bg-white">
      {data?.summerSale?.isActive && !data?.settings?.hideSummerSale && (
        <SummerSaleTopBanner
          data={data.summerSale}
          ctaUrl={summerSaleCtaUrl}
          language={data.settings?.language}
        />
      )}
      {data?.summerSale?.isActive && !data?.settings?.hideSummerSale && (
        <SummerSalePopup
          data={data.summerSale}
          ctaUrl={summerSaleCtaUrl}
          language={data.settings?.language}
        />
      )}
      <AffiliateBanners
        settings={data?.settings}
        ctaUrl={affiliateCtaUrl}
        enableFlashSale
      />
      <LaunchEventWidget
        data={data?.launchEvent}
        language={data?.settings?.language}
        affCtaUrl={affiliateCtaUrl}
      />
      {!launchEventLive && (
        <TradingCompetitionPopup
          data={data?.tradingCompetitionPopup}
          language={data?.settings?.language}
          affCtaUrl={affiliateCtaUrl}
        />
      )}
      <style jsx global>{`
        :root {
          font-size: calc(700vw / 2200);
        }

        @media (max-width: 1920px) {
          :root {
            font-size: calc(700vw / 1920);
          }
        }

        @media (max-width: 992px) {
          :root {
            font-size: 50%;
          }
        }

        @media (max-width: 639px) {
          :root {
            font-size: calc(800vw / 435);
          }
        }
      `}</style>

      {data?.heroVersion === 'v2' ? (
        <HeroSectionV2 data={data} />
      ) : (
        <HeroSection data={data} />
      )}

      <VipDealSection data={data} />

      <IntroSection data={data} />

      {data?.pricingSectionV3 ? (
        <NewPricingSection
          sectionV3={data.pricingSectionV3}
          section={data.pricingSectionV2}
          language={data.settings?.language}
          fixedAsset={pricingAssetKey && pricingAssetKey !== 'both' ? pricingAssetKey : undefined}
          checkoutHrefOverride={affiliateCtaUrl}
        />
      ) : pricingSection ? (
        <section className="relative w-full pt-[10rem] pb-[10rem] lg:pb-[20rem]">
          <div className="mx-auto w-[min(190rem,92%)] flex flex-col gap-[6rem]">
            <PricingHeroBlock
              data={pricingSection}
              language={data.settings?.language}
            />
            <PricingTiersBlock
              data={pricingSection}
              language={data.settings?.language}
              discount={data?.pricingV2Discount}
              filter={pricingFilter}
            />
            {pricingSection.comparison &&
            pricingSection.specsCatalog?.length ? (
              <PricingComparisonBlock
                data={pricingSection}
                language={data.settings?.language}
                discount={data?.pricingV2Discount}
                filter={pricingFilter}
              />
            ) : null}
            {pricingSection.platformsFooter && (
              <PricingPlatformsBlock
                data={pricingSection.platformsFooter}
                language={data.settings?.language}
                filter={pricingFilter}
              />
            )}
          </div>
        </section>
      ) : (
        <DealsSection data={data} />
      )}

      {/* <div className="flex overflow-hidden items-center w-full z-[9] py-[3.5rem] border-y">
        {marqueeItems && (
          <div
            className="flex flex-shrink-0 items-center"
            style={{
              animation: `scrollLeft ${data!.heroPills!.length * 20}s linear infinite`,
              willChange: 'transform',
              transform: 'translateZ(0)',
            }}
          >
            {marqueeItems}
          </div>
        )}
      </div> */}
      <ScrollingBanner
        className="bg-transparent border-y-0"
        words={marqueeItems?.map(
          (item) => item.props.children[0].props.children,
        )}
      />

      <StepsSection data={data} />

      {(data?.tradersSayChip ||
        data?.tradersSayHeading ||
        data?.tradersSayItems?.length ||
        data?.tradersSayCTA) && <TradersSaySection data={data} />}

      <ComparisonSection data={data} />

      {(data?.testimonialsChip ||
        data?.testimonialsHeading ||
        data?.testimonialsRating ||
        data?.testimonialsRatingText ||
        data?.testimonialsItems?.length) && <TestimonialsSection data={data} />}

      {(data?.faqChip || data?.faqHeading || data?.faqItems?.length) && (
        <FAQSection data={data} />
      )}

      {(data?.finalCtaChip ||
        data?.finalCtaHeading ||
        data?.finalCtaSubheading ||
        data?.finalCtaButton ||
        data?.finalCtaImage) && <FinalCTASection data={data} />}

      {data?.settings?.footer && (
        <FooterFabio
          data={data.settings.footer}
          slug={data?.settings?.slug?.current}
        />
      )}
    </main>
  );
}
